import classNames from "classnames";
import type React from "react";
import { useEffect, useState } from "react";
import reactElementToJSXString from "react-element-to-jsx-string";
import Tooltip from "../Common/Tooltip";

type Props = {
    className?: string;
    children?: React.ReactNode;
    style?: React.CSSProperties;
};

async function copyToClipboard(text: string) {
    try {
        await navigator.clipboard.writeText(text);

        console.log(text);
    } catch (e) {
        console.log("Not supported", e);
    }
}

export function CopyToClipboard(props: Props) {
    const { children, className, style } = props;
    const jsx = reactElementToJSXString(children);
    return (
        <div
            className={classNames(
                className,
                "web-lib__tooltip",
                "web-lib__tooltip--top-left",
            )}
            data-tooltip={jsx}
            style={style || { display: "table" }}
            onClick={(e) => {
                copyToClipboard(jsx);
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            {children}
        </div>
    );
}

export function Title(props: Props) {
    const { children, className } = props;
    return (
        <div className={classNames("component-library__title", className)}>
            {children}
        </div>
    );
}

export function Subtitle(props: Props) {
    const { children, className } = props;
    return (
        <div className={classNames("component-library__subtitle", className)}>
            {children}
        </div>
    );
}

const blockTitleCssClass = "component-library__block-title";
export function Block(props: Props & { title: string; description?: string }) {
    const { description, title, children, className } = props;
    return (
        <div
            id={`block_${title
                .toLowerCase()
                .replaceAll(" ", "_")
                .replaceAll("&", "_")}`}
            className={classNames("component-library__block", className)}
        >
            <div className={blockTitleCssClass}>{title}</div>
            <div>{description}</div>
            {children}
        </div>
    );
}

export function Spacer(props: { width?: number; height?: number }) {
    const { width, height } = props;
    return (
        <div
            style={{
                display: "inline-block",
                width: width ?? 10,
                height: height ?? 10,
            }}
        />
    );
}

export function GroupBlock(props: Props) {
    const { children } = props;
    return <div className="component-library__group-block">{children}</div>;
}

export function ColorBlock(props: Props & { color: string }) {
    const { color, className } = props;
    return (
        <>
            <Tooltip label={color} placement="top-left">
                <div
                    className={classNames(
                        "component-library__preview-block",
                        className,
                    )}
                    style={{ backgroundColor: color }}
                    data-tooltip={color}
                    onClick={() => {
                        copyToClipboard(color);
                    }}
                />
            </Tooltip>
            <Spacer />
        </>
    );
}

export function ShadowBlock(props: Props & { boxShadow: string }) {
    const { boxShadow, className } = props;
    const copy = `box-shadow: ${boxShadow};`;
    return (
        <>
            <Tooltip label={copy} placement="top-left">
                <div
                    className={classNames(
                        "component-library__preview-block",
                        className,
                    )}
                    style={{
                        boxShadow,
                        backgroundColor: "white",
                    }}
                    data-tooltip={boxShadow}
                    onClick={() => {
                        copyToClipboard(copy);
                    }}
                />
            </Tooltip>
            <Spacer width={50} />
        </>
    );
}

type BlockAnchor = {
    anchorId: string;
    title: string;
};
export function TableOfContents() {
    const [contents, setContents] = useState<BlockAnchor[]>([]);
    useEffect(() => {
        const anchors: BlockAnchor[] = [];
        const blockElements = document.querySelectorAll("[id^='block_']");

        if (blockElements) {
            blockElements.forEach((block) => {
                const anchorId = block.getAttribute("id");

                if (anchorId) {
                    const blockTitleElement = document.querySelector(
                        `#${anchorId} .${blockTitleCssClass}`,
                    );

                    if (blockTitleElement) {
                        anchors.push({
                            anchorId,
                            title: blockTitleElement.innerHTML,
                        });
                    }
                }
            });
        }

        setContents(anchors);
    }, []);

    return (
        <div className="component-library__table-of-contents">
            <strong>Contents</strong>
            {contents && (
                <ul>
                    {contents.map(({ anchorId, title }) => (
                        <li key={anchorId}>
                            <a href={`#${anchorId}`}>{title}</a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
